import { InvalidParametersError } from '@libp2p/interface'
import { getNetConfig } from '@libp2p/utils'
import { CODE_CERTHASH, multiaddr } from '@multiformats/multiaddr'
import { base64url } from 'multiformats/bases/base64'
import { bases, digest } from 'multiformats/basics'
import * as Digest from 'multiformats/hashes/digest'
import { sha256 } from 'multiformats/hashes/sha2'
import { MAX_MESSAGE_SIZE } from '../../constants.js'
import { InvalidFingerprintError, UnsupportedHashAlgorithmError } from '../../error.js'
import type { Multiaddr } from '@multiformats/multiaddr'
import type { MultihashDigest } from 'multiformats/hashes/interface'

/**
 * Get base2 | identity decoders
 */
// @ts-expect-error - Not easy to combine these types.
export const multibaseDecoder: any = Object.values(bases).map(b => b.decoder).reduce((d, b) => d.or(b))

const fingerprintRegex = /^a=fingerprint:(?:\w+-[0-9]+)\s(?<fingerprint>(:?[0-9a-fA-F]{2})+)$/m
export function getFingerprintFromSdp (sdp: string | undefined): string | undefined {
  if (sdp == null) {
    return undefined
  }

  const searchResult = sdp.match(fingerprintRegex)
  return searchResult?.groups?.fingerprint
}

// Extract the certhash from a multiaddr
export function certhash (ma: Multiaddr): string {
  const components = ma.getComponents()
  const certhash = components.find(c => c.code === CODE_CERTHASH)?.value

  if (certhash === undefined || certhash === '') {
    throw new InvalidParametersError(`Couldn't find a certhash component of multiaddr: ${ma.toString()}`)
  }

  return certhash
}

/**
 * Convert a certhash into a multihash
 */
export function decodeCerthash (certhash: string): MultihashDigest {
  return digest.decode(multibaseDecoder.decode(certhash))
}

export function certhashToFingerprint (certhash: string): string {
  const multibaseDecoded = decodeCerthash(certhash)

  return new Array(multibaseDecoded.bytes.length)
    .fill(0)
    .map((val, index) => {
      return multibaseDecoded.digest[index].toString(16).padStart(2, '0').toUpperCase()
    })
    .join(':')
}

/**
 * Extract the fingerprint from a multiaddr
 */
export function ma2Fingerprint (ma: Multiaddr): string {
  const multihashDecoded = decodeCerthash(certhash(ma))
  const prefix = toSupportedHashFunction(multihashDecoded.code)
  const fingerprint = multihashDecoded.digest.reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '')
  const sdp = fingerprint.match(/.{1,2}/g)

  if (sdp == null) {
    throw new InvalidFingerprintError(fingerprint, ma.toString())
  }

  return `${prefix} ${sdp.join(':').toUpperCase()}`
}

export function fingerprint2Ma (fingerprint: string): Multiaddr {
  const output = fingerprint.split(':').map(str => parseInt(str, 16))
  const encoded = Uint8Array.from(output)
  const digest = Digest.create(sha256.code, encoded)

  return multiaddr(`/certhash/${base64url.encode(digest.bytes)}`)
}

/**
 * Normalize the hash name from a given multihash has name
 */
export function toSupportedHashFunction (code: number): 'sha-1' | 'sha-256' | 'sha-512' {
  switch (code) {
    case 0x11:
      return 'sha-1'
    case 0x12:
      return 'sha-256'
    case 0x13:
      return 'sha-512'
    default:
      throw new UnsupportedHashAlgorithmError(code)
  }
}

/**
 * Create an answer SDP message from a multiaddr - the server always operates in
 * ice-lite mode and DTLS active mode.
 */
export function serverAnswerFromMultiaddr (ma: Multiaddr, ufrag: string): RTCSessionDescriptionInit {
  const { host, port, type } = getNetConfig(ma)

  if (type !== 'ip4' && type !== 'ip6') {
    throw new InvalidParametersError(`Multiaddr ${ma} was not an IPv4 or IPv6 address`)
  }

  const fingerprint = ma2Fingerprint(ma)
  const sdp = `v=0
o=- 0 0 IN IP${type === 'ip4' ? 4 : 6} ${host}
s=-
t=0 0
a=ice-lite
m=application ${port} UDP/DTLS/SCTP webrtc-datachannel
c=IN IP${type === 'ip4' ? 4 : 6} ${host}
a=mid:0
a=ice-options:ice2
a=ice-ufrag:${ufrag}
a=ice-pwd:${ufrag}
a=fingerprint:${fingerprint}
a=setup:passive
a=sctp-port:5000
a=max-message-size:${MAX_MESSAGE_SIZE}
a=candidate:1467250027 1 UDP 1467250027 ${host} ${port} typ host
a=end-of-candidates
`

  return {
    type: 'answer',
    sdp
  }
}

/**
 * Create an offer SDP message from a multiaddr
 */
export function clientOfferFromMultiAddr (ma: Multiaddr, ufrag: string): RTCSessionDescriptionInit {
  const { host, port, type } = getNetConfig(ma)

  if (type !== 'ip4' && type !== 'ip6') {
    throw new InvalidParametersError(`Multiaddr ${ma} was not an IPv4 or IPv6 address`)
  }

  const sdp = `v=0
o=- 0 0 IN IP${type === 'ip4' ? 4 : 6} ${host}
s=-
c=IN IP${type === 'ip4' ? 4 : 6} ${host}
t=0 0
a=ice-options:ice2,trickle
m=application ${port} UDP/DTLS/SCTP webrtc-datachannel
a=mid:0
a=setup:active
a=ice-ufrag:${ufrag}
a=ice-pwd:${ufrag}
a=fingerprint:sha-256 00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00
a=sctp-port:5000
a=max-message-size:${MAX_MESSAGE_SIZE}
a=candidate:1467250027 1 UDP 1467250027 ${host} ${port} typ host
a=end-of-candidates
`

  return {
    type: 'offer',
    sdp
  }
}

/**
 * Replace (munge) the ufrag and password values in a SDP
 */
export function munge (desc: RTCSessionDescriptionInit, ufrag: string): RTCSessionDescriptionInit {
  if (desc.sdp === undefined) {
    throw new InvalidParametersError("Can't munge a missing SDP")
  }

  const lineBreak = desc.sdp.includes('\r\n') ? '\r\n' : '\n'

  try {
    desc.sdp = desc.sdp
      .replace(/\na=ice-ufrag:[^\n]*\n/, '\na=ice-ufrag:' + ufrag + lineBreak)
      .replace(/\na=ice-pwd:[^\n]*\n/, '\na=ice-pwd:' + ufrag + lineBreak)
  } catch {
    // fails under Node.js
  }

  return desc
}
